import { Grid } from '@chakra-ui/react';

import type { HomeStatsWidgetId } from 'types/homepage';

import config from 'configs/app';
import useApiQuery from 'lib/api/useApiQuery';
import { HOMEPAGE_STATS, HOMEPAGE_STATS_MICROSERVICE } from 'stubs/stats';
import type { Props as StatsWidgetProps } from 'ui/shared/stats/StatsWidget';
import StatsWidget from 'ui/shared/stats/StatsWidget';

const isStatsFeatureEnabled = config.features.stats.isEnabled;

const Stats = () => {
  // data from stats microservice is prioritized over data from stats api
  const statsQuery = useApiQuery('stats_main', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: isStatsFeatureEnabled ?
        HOMEPAGE_STATS_MICROSERVICE :
        undefined,
      enabled: isStatsFeatureEnabled,
    },
  });

  // const customStatsQuery = useApiQuery('custom_stats');

  const apiQuery = useApiQuery('stats', {
    queryOptions: {
      refetchOnMount: false,
      placeholderData: HOMEPAGE_STATS,
    },
  });

  const isPlaceholderData =
    statsQuery.isPlaceholderData || apiQuery.isPlaceholderData;

  const isLoading = isPlaceholderData;

  interface Item extends StatsWidgetProps {
    id: HomeStatsWidgetId;
  }

  const apiData = apiQuery.data;
  const statsData = statsQuery.data;

  const items: Array<Item> = (() => {
    if (!statsData && !apiData) {
      return [];
    }

    return (
      [
        (statsData?.total_transactions?.value ||
          apiData?.total_transactions) && {
          id: 'total_txs' as const,
          icon: 'transactions_slim' as const,
          label: 'Total transactions',
          value: Number(
            statsData?.total_transactions?.value || apiData?.total_transactions,
          ).toLocaleString(),
          href: { pathname: '/txs' as const },
          isLoading,
        },

        (statsData?.total_addresses?.value || apiData?.total_addresses) && {
          id: 'wallet_addresses' as const,
          icon: 'wallet' as const,
          label: statsData?.total_addresses?.title || 'Wallet addresses',
          value: Number(
            statsData?.total_addresses?.value || apiData?.total_addresses,
          ).toLocaleString(),
          isLoading,
        },
      ]
        .filter(Boolean)
        // .filter(({ id }) => config.UI.homepage.stats.includes(id))
        .sort((a, b) => {
          const indexA = config.UI.homepage.stats.indexOf(a.id);
          const indexB = config.UI.homepage.stats.indexOf(b.id);
          if (indexA > indexB) {
            return 1;
          }
          if (indexA < indexB) {
            return -1;
          }
          return 0;
        })
    );
  })();

  if (items.length === 0) {
    return null;
  }
  return (
    <Grid
      gridTemplateColumns="1fr 1fr"
      gridGap={{ base: '16px' }}
      flexBasis="50%"
      flexGrow={ 1 }
    >
      { items.map((item, index) => (
        <StatsWidget
          key={ item.id }
          { ...item }
          isLoading={ isLoading }
          href={ item.href }
          _last={
            items.length % 2 === 1 && index === items.length - 1 ?
              { gridColumn: 'span 2' } :
              undefined
          }
        />
      )) }
    </Grid>
  );
};

export default Stats;
